/** Helpers for translating document tree node names.
 *
 * Uses fixed mappings for:
 * - level 1 (document categories like FAQs, privacy policy)
 * - level 2 (product-type based nodes)
 *
 * Translation is applied by walking the `DocumentTreeItem` tree recursively and
 * returning a new translated tree.
 */
import i18next from 'i18next';
import { DocumentTreeItem } from '../components/common/CommonTypes';
import { ProductTypes } from '../consts/Common';

export const DocumentTreeLevel1 = {
  FAQs: 'FAQs',
  InitialDisclosure: 'InitialDiscolusure',
  InsuranceProductInformation: 'InsuranceProducInformation',
  Policy: 'Policy',
  PrivacyPolicy: 'PrivacyPolicy',
  TermsAndConditions: 'WebsiteTermsAndConditions'
};

const Level1DocumentTranslationMapping: { [key: string]: string } = {
  [DocumentTreeLevel1.FAQs]: 'page-info.documents.faq-title',
  [DocumentTreeLevel1.InitialDisclosure]:
    'page-info.documents.initial-disclosure-title',
  [DocumentTreeLevel1.InsuranceProductInformation]:
    'page-info.documents.insurance-product-info-title',
  [DocumentTreeLevel1.Policy]: 'page-info.documents.terms-conditions-title',
  [DocumentTreeLevel1.PrivacyPolicy]:
    'page-info.documents.privacy-policy-title',
  [DocumentTreeLevel1.TermsAndConditions]:
    'page-info.documents.terms-conditions-title'
};

const Level2DocumentTranslationMapping: { [key: string]: string } = {
  [ProductTypes.Alloy]: 'page-info.documents.alloy-wheel-insurance',
  [ProductTypes.AssistancePlus]: 'page-info.documents.assistance-plus',
  [ProductTypes.Cosmetic]: 'page-info.documents.cosmetic-repair-insurance',
  [ProductTypes.Gap]: 'page-info.documents.gap-insurance',
  [ProductTypes.MOT]: 'page-info.documents.mot-cover',
  [ProductTypes.RoadsideAssistance]: 'page-info.documents.roadside-assistance',
  [ProductTypes.ServicePlan]: 'page-info.documents.service-plan',
  [ProductTypes.Tyre]: 'page-info.documents.tyre-insurance',
  [ProductTypes.Warranty]: 'page-info.documents.warranty'
};

export function translateDocumentNames(documents: DocumentTreeItem[]) {
  return documents.map((doc) => translateNode(doc));
}

function translateNode(node: DocumentTreeItem): DocumentTreeItem {
  let name = node.name;

  const translationKey1 = Level1DocumentTranslationMapping[node.id];
  if (node.level === 1 && translationKey1) {
    name = i18next.t(translationKey1, { defaultValue: node.name });
  }

  const translationKey2 = Level2DocumentTranslationMapping[node.typeId!];
  if (node.level === 2 && translationKey2) {
    name = i18next.t(translationKey2, { defaultValue: node.name });
  }

  const children = Array.isArray(node.children)
    ? node.children.map((child) => translateNode(child))
    : node.children;

  return {
    ...node,
    name, // consider saving a translation key if dynamic translation is needed later
    children
  };
}
